import { useEffect, useState, useCallback } from 'react'
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useLocation,
} from '@tanstack/react-router'
import type { ExecutiveSummaryFilters } from '@workspace/react-query'
import { useIncidentReportFilters } from '@workspace/react-query'
import { Tabs, TabsList, TabsTrigger } from '@workspace/ui/components/tabs'
import { authClient } from '@/lib/auth'
import { useTopNav } from '@/context/top-nav-provider'
import { Main } from '@/components/layout/main'
import { ReportFilters } from '@/features/incidents/reports/components/report-filters'
import { ReportsProvider } from '@/features/incidents/reports/context/reports-context'

const defaultFilters: ExecutiveSummaryFilters = {
  dateStart: '',
  dateEnd: '',
  incidentType: '',
  location: '',
  severity: '',
  reportedBy: '',
}

export const Route = createFileRoute(
  '/_authenticated/_organization/incidents/reports'
)({
  component: ReportsLayout,
})

function ReportsLayout() {
  const navigate = useNavigate()
  const { data: activeOrganization, isPending: isLoadingOrganization } =
    authClient.useActiveOrganization()
  const { setLinks, setFixed } = useTopNav()
  const [filters, setFilters] =
    useState<ExecutiveSummaryFilters>(defaultFilters)

  // Determine active tab from location using TanStack Router's useLocation
  const location = useLocation()
  const activeTab = (() => {
    const pathname = location.pathname
    if (pathname.includes('trend-analysis')) return 'trend-analysis'
    if (pathname.includes('operational-performance'))
      return 'operational-performance'
    if (pathname.includes('compliance')) return 'compliance'
    if (pathname.includes('incident-mapping')) return 'incident-mapping'
    return 'executive-summary'
  })()

  // Set breadcrumbs
  useEffect(() => {
    setLinks([
      { title: 'Overview', href: '/' },
      { title: 'Incidents', href: '/incidents' },
      {
        title: 'Reports',
        href: '/incidents/reports/executive-summary',
        isActive: true,
      },
    ])
    setFixed(true)
    return () => {
      setLinks([])
    }
  }, [setLinks, setFixed])

  // Fetch filter options
  const { data: filterOptionsResponse, isLoading: isLoadingFilterOptions } =
    useIncidentReportFilters(activeOrganization?.id)

  // Filter handlers
  const handleFilterChange = useCallback(
    (name: keyof ExecutiveSummaryFilters, value: string) => {
      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }))
    },
    []
  )

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters)
  }, [])

  const handleTabChange = (value: string) => {
    navigate({
      to: `/incidents/reports/${value}`,
    })
  }

  // Loading state for organization
  if (isLoadingOrganization) {
    return (
      <Main className='flex flex-1 flex-col gap-6'>
        <div className='flex animate-pulse flex-col gap-2'>
          <div className='bg-muted h-8 w-64 rounded' />
          <div className='bg-muted h-4 w-96 rounded' />
        </div>
      </Main>
    )
  }

  if (!activeOrganization) {
    return null
  }

  const contextValue = {
    filters,
    filterOptions: filterOptionsResponse?.data,
    isLoadingFilterOptions,
    organizationId: activeOrganization?.id,
  }

  return (
    <ReportsProvider value={contextValue}>
      <Main className='mx-0 sm:mx-4 lg:mx-12 xl:mx-20 flex flex-1 flex-col gap-6' fluid>
        {/* Header */}
        <div className='flex flex-wrap items-end justify-between gap-2'>
          <div>
            <h1 className='text-2xl font-bold tracking-tight'>
              Incident Reports
            </h1>
            <p className='text-muted-foreground'>
              Comprehensive analysis and insights across all incidents
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value='executive-summary'>
              Executive Summary
            </TabsTrigger>
            <TabsTrigger value='operational-performance'>
              Operational Performance
            </TabsTrigger>
            <TabsTrigger value='compliance'>Compliance</TabsTrigger>
            <TabsTrigger value='incident-mapping'>Incident Mapping</TabsTrigger>
            <TabsTrigger value='trend-analysis'>Trend Analysis</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Filters */}
        <ReportFilters
          filters={filters}
          filterOptions={filterOptionsResponse?.data}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          isLoading={isLoadingFilterOptions}
        />

        {/* Tab Content */}
        <Outlet />
      </Main>
    </ReportsProvider>
  )
}
